import { ProductSourceDtoSourceEnum } from "definitions/product-storage";
import { useEffect, useMemo, useState } from "react";

import { useProductDetailWithPrice } from "../hooks";
import {
  collectProductStores,
  getActiveVariant,
  getValuesWithoutStore,
  groupOffersByStore,
  summarizeProductPrices,
} from "../utils";

export const useProductVariantsController = (
  id: string | null | undefined,
  platform: ProductSourceDtoSourceEnum | null,
) => {
  const { variants, isLoading, hasLoadError, reload } =
    useProductDetailWithPrice(id, platform);
  const [selected, setSelected] = useState<Record<string, string>>({});
  const [selectedStore, setSelectedStore] = useState<string | null>(null);

  useEffect(() => {
    if (!variants) return;

    const initial: Record<string, string> = {};
    Object.entries(variants.attributes).forEach(
      ([attributeName, attributeValues]) => {
        if (attributeValues.length > 0) {
          initial[attributeName] = attributeValues[0];
        }
      },
    );

    setSelected(initial);
    setSelectedStore(null);
  }, [variants]);

  const activeVariant = useMemo(() => {
    if (!variants) return null;
    return getActiveVariant(variants, selected);
  }, [variants, selected]);

  const stores = useMemo(() => collectProductStores(variants), [variants]);

  const valuesWithoutStore = useMemo(
    () => getValuesWithoutStore(variants, selectedStore),
    [variants, selectedStore],
  );

  const variantStores = useMemo(
    () => groupOffersByStore(activeVariant?.offers),
    [activeVariant],
  );

  const activeStore = variantStores.some(
    (group) => group.store === selectedStore,
  )
    ? selectedStore
    : null;

  const storeGroups = useMemo(
    () =>
      activeStore
        ? variantStores.filter((group) => group.store === activeStore)
        : variantStores,
    [variantStores, activeStore],
  );

  const priceSummary = useMemo(
    () => summarizeProductPrices(storeGroups),
    [storeGroups],
  );

  const hasPrices = Boolean(
    variants && Object.keys(variants.variants).length > 0,
  );

  return {
    variants,
    isLoading,
    hasLoadError,
    hasPrices,
    selected,
    setSelected,
    activeVariant,
    reload,
    stores,
    selectedStore: activeStore,
    setSelectedStore,
    priceSummary,
    valuesWithoutStore,
    variantStores,
    storeGroups,
  };
};
